import Model, { attr } from '@warp-drive/legacy/model';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import type Section from '@linc-technologies/linced-common/models/enrolments/section';

// Proto: enrolments.Schema.
export default class Schema extends Model {
  declare [Type]: 'enrolments/schema';

  // Model Attributes
  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the unique identifier of the school obtaining
  @attr('string', { defaultValue: '' })
  declare schoolId: string;

  // schoolCode is the code issued by an educational authority.
  @attr('string', { defaultValue: '' })
  declare schoolCode: string;

  // schoolName is the name of the School.
  @attr('string', { defaultValue: '' })
  declare schoolName: string;

  // schoolLogoUrl stores the hyperlink to the school logo.
  @attr('string', { defaultValue: '' })
  declare schoolLogoUrl: string;

  // jurisdiction stores the School's Jurisdiction, to be fed into Applications.
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // campusId is the unique identitfier of the School's Campus.
  // TODO: implement when we have Campuses
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  // campusCode is the short code assigned for the campus. For example, 'PRI'
  @attr('string', { defaultValue: '' })
  declare campusCode: string;

  // campusName is the name of the Schools Campus for which they are doing
  // enrolments for.
  @attr('string', { defaultValue: '' })
  declare campusName: string;

  // countryCode is the ISO 'Alpha 2' code for the country the school resides
  // within.
  @attr('string', { defaultValue: '' })
  declare countryCode: string;

  // enrolmentYear is the year for which the enrolment intake is for.
  @attr('number', { defaultValue: 0 })
  declare enrolmentYear: number;

  // enrolmentsOpen provides a switch for schools that prefer to be reactive.
  @attr('boolean', { defaultValue: false })
  declare enrolmentsOpen: boolean;

  // dateEnrolmentsOpen contains a 'yyyy-mm-dd' date string as to when
  // enrolments will open.
  @attr('date')
  declare dateEnrolmentsOpens: Date | undefined;

  // dateEnrolmentsClose contains a 'yyyy-mm-dd' date string as to when
  // enrolments will close.
  @attr('date')
  declare dateEnrolmentsClose: Date | undefined;

  // introduction provides an overall welcome to the school's enrolment page.
  @attr('string', { defaultValue: '' })
  declare introduction: string;

  // sections contain a conceptual whole of data needed to enrol a student.
  @fragmentArray('enrolments/section')
  declare sections: FragmentArray<Section>;

  // primaryContacts contains schema metadata relating to primary contacts.
  @fragment('enrolments/section')
  declare primaryContacts: Section | null;

  // emergencyContacts contains schema metadata relating to secondary contacts.
  @fragment('enrolments/section')
  declare emergencyContacts: Section | null;

  // medicalContacts contains schema metadata relating to medical contacts.
  @fragment('enrolments/section')
  declare medicalContacts: Section | null;

  // medicalConditions contains schema metadata relating to medical conditions.
  @fragment('enrolments/section')
  declare medicalConditions: Section | null;
}
